import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IcoX, IcoCheckCircle, IcoCheck, IcoAlertTriangle } from '../dashboard/DashIcons';

function SetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [managerInfo, setManagerInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('verifying'); // verifying | ready | submitting | success | error
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('No invite token found. Please use the link from your invitation email.');
      return;
    }

    const verifyToken = async () => {
      try {
        const res = await fetch(`/api/invite/verify?token=${token}`);
        const data = await res.json();
        if (res.ok) {
          setManagerInfo(data);
          setStatus('ready');
        } else {
          setStatus('error');
          setError(data.message || 'Invalid or expired invite link.');
        }
      } catch (err) {
        setStatus('error');
        setError('Something went wrong. Please try again.');
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setStatus('submitting');

    try {
      const res = await fetch('/api/invite/set-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password })
      });

      const data = await res.json();

      if (res.ok) {
        setStatus('success');
      } else {
        setStatus('ready');
        setError(data.message || 'Error setting password.');
      }
    } catch (err) {
      setStatus('ready');
      setError('Something went wrong. Please try again.');
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '2rem'
  };

  const cardStyle = {
    background: 'white',
    borderRadius: '16px',
    padding: '2.5rem',
    width: '100%',
    maxWidth: '440px',
    boxShadow: '0 25px 50px rgba(0,0,0,0.25)'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '1rem',
    boxSizing: 'border-box',
    outline: 'none'
  };

  const btnStyle = {
    width: '100%',
    padding: '0.875rem',
    background: 'linear-gradient(135deg, #22c55e 0%, #0f4d2e 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '700',
    cursor: 'pointer',
    marginTop: '0.5rem'
  };

  // ── Verifying ──
  if (status === 'verifying') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <div style={{ fontSize: '2rem', marginBottom: '1rem' }}>⏳</div>
            <p style={{ color: '#6b7280', fontSize: '1rem' }}>Verifying your invite link...</p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error (invalid/expired token) ──
  if (status === 'error') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '1rem' }}><IcoX size={48} color="#ef4444"/></div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem', color: '#111827' }}>
              Invalid Link
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '1.5rem', lineHeight: '1.6' }}>{error}</p>
            <p style={{ color: '#9ca3af', fontSize: '0.875rem' }}>
              Contact your administrator to send a new invitation.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Success ──
  if (status === 'success') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '1rem' }}><IcoCheckCircle size={48} color="#22c55e"/></div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.75rem', color: '#111827' }}>
              Password Set!
            </h2>
            <p style={{ color: '#6b7280', marginBottom: '2rem', lineHeight: '1.6' }}>
              Your account is ready. You can now log in to ClockEarnings.
            </p>
            <button onClick={() => navigate('/login')} style={btnStyle}>
              Go to Login →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Ready / Submitting ──
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.75rem', fontWeight: '800', margin: 0, color: '#0f4d2e' }}>
            Clock<span style={{ color: '#22c55e' }}>Earnings</span>
          </h1>
        </div>

        <h2 style={{ fontSize: '1.375rem', fontWeight: '700', color: '#111827', marginBottom: '0.5rem' }}>
          Set Your Password
        </h2>
        {managerInfo && (
          <p style={{ color: '#6b7280', fontSize: '0.95rem', marginBottom: '1.75rem' }}>
            Welcome, <strong>{managerInfo.firstName}</strong>! Create a password for <strong>{managerInfo.email}</strong>
          </p>
        )}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
            New Password
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters"
                required
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '0.875rem' }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          <label style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.95rem' }}>
            Confirm Password
            <div style={{ position: 'relative' }}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
                style={inputStyle}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{ position: 'absolute', right: '0.75rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: '0.875rem' }}
              >
                {showPassword ? 'Hide' : 'Show'}
              </button>
            </div>
          </label>

          {/* Password strength hint */}
          {password.length > 0 && (
            <div style={{ fontSize: '0.8rem', color: password.length >= 8 ? '#10b981' : '#f59e0b', fontWeight: '600' }}>
              {password.length >= 8 ? <><IcoCheck size={12} color="#059669"/> Good password length</> : <><IcoAlertTriangle size={12} color="#d97706"/> {8 - password.length} more characters needed</>}
            </div>
          )}

          {error && (
            <div style={{ padding: '0.75rem 1rem', background: '#fee2e2', color: '#991b1b', borderRadius: '6px', fontSize: '0.875rem', fontWeight: '600' }}>
              <><IcoX size={12} color="#ef4444"/> {error}</>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            style={{ ...btnStyle, opacity: status === 'submitting' ? 0.7 : 1, cursor: status === 'submitting' ? 'not-allowed' : 'pointer' }}
          >
            {status === 'submitting' ? '⏳ Setting Password...' : 'Set Password & Activate Account'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default SetPassword;